import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import DeleteIcon from '@mui/icons-material/Delete'
import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import EditIcon from '@mui/icons-material/Edit'
import GridOnIcon from '@mui/icons-material/GridOn'
import SaveIcon from '@mui/icons-material/Save'
import WarningIcon from '@mui/icons-material/Warning'
import { Box, Button, Card, Grid, IconButton, Typography } from '@mui/material'
import { nanoid } from '@reduxjs/toolkit'
import { message, Table, TableColumnsType } from 'antd'
import dayjs from 'dayjs'
import { chunk, set, uniq } from 'lodash'
import { isEmpty } from 'radash'
import React, { useCallback, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { read, utils } from 'xlsx'
import {
  closeModal,
  openModal,
} from '../../../../../store/reducers/modal/modal.slice'
import ConfirmBlackListModal from '../../../../components/ConfirmBlackListModal'
import { ContentWrapper } from '../../../../components/ContentWrapper'
import Helmet from '../../../../components/Helmet'
import HeaderContent from '../../../../components/Layouts/HeaderContent'
import { ERRORS_MESSAGE_MAPPER } from '../../../../constants/errors'
import { DATE_FORMAT_INTERNATIONAL } from '../../../../constants/global'
import PageUrls from '../../../../constants/page-urls'
import { axiosInstance } from '../../../../hooks/useAxios'
import OrderFormImport from './components/OrderFromImport'
import { useRemoteArea } from '../../../../hooks/useRemoteArea'
import { useUser } from '../../../../hooks/useUser'
import { AllowShippingProviderLists } from '../../../../utils/provider'
import { cleanPhone, delayTime, numberFormat } from '../../../../utils'
import CircularLoading from '../../../../components/CircularProgress'

const Menus = [
  {
    breadcrumbName: 'รายการคำสั่งซื้อ',
    title: 'รายการคำสั่งซื้อ',
    path: PageUrls.Orders.Index,
  },
]

enum OrderExcelStatus {
  Success = 'success',
  Warning = 'warning',
}

export interface IOrderListExcel {
  key?: number
  keyId?: string
  product: string
  quantity: string
  discount: string
  size: string
  name: string
  phone: string
  free: string
  address: string
  district: string
  subdistrict: string
  province: string
  zipcode: string
  facebook?: string
  cod_bank: OrderExcelStatusPayment | string
  total: number | string
  actor: string
  date: string
  channel: string
  telesale: string
  status?: OrderExcelStatus
  message?: string
  field?: string
  isRemote?: boolean
  isToure?: boolean
}

enum OrderExcelStatusPayment {
  COD = 'COD',
  BANK = 'BANK',
}

interface IBlacklist {
  name: string
  phone: string
}

const OrderImportExcelPage = () => {
  const { merchant } = useUser()
  const [orderList, setOrderList] = useState<IOrderListExcel[]>([])
  const [editRow, setEditRow] = useState<number | null>(null)
  const [hasDuplicate, setHasDuplicate] = useState<boolean>(false)
  const [editOrder, setEditOrder] = useState<IOrderListExcel | null>(null)
  const [popupLoading, setPopupLoading] = useState<boolean>(false)
  const [popupMessage, setPopupMessage] = useState<string>('')
  const inputFile = useRef<HTMLInputElement>(null)
  const dispatch = useDispatch()

  const { data } = useRemoteArea()

  const flashProvider = AllowShippingProviderLists(merchant).find(
    (provider) => provider.value === 'flash',
  )

  const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]

    if (file) {
      const reader = new FileReader()
      reader.onload = (event) => {
        const wb = read(event.target!.result)
        const sheets = wb.SheetNames
        if (sheets.length) {
          const rows: any[] = utils.sheet_to_json(wb.Sheets[sheets[0]])
          const lowerCaseKeys = rows
            .map((row) => {
              const subdistrictMatch = row['Address']?.match(
                /(ต\.|ตำบล|แขวง)\s*([^\s]+)/,
              )
              //console.log('subdistrictMatch', subdistrictMatch)
              const address = subdistrictMatch
                ? row['Address'].replace(subdistrictMatch?.[0] ?? '', '').trim()
                : row['Address']?.trim() || ''

              const province = row['Province']?.trim() || ''
              const prefix = subdistrictMatch?.[1] ?? ''
              let subdistrict = subdistrictMatch
                ? subdistrictMatch?.[2] ?? ''
                : ''

              if (prefix === 'แขวง' || province === 'กรุงเทพมหานคร') {
                subdistrict = `แขวง${subdistrict}`
              }

              const cod_bank = ['bank', 'BANK', 'Bank'].includes(
                row['COD_BANK'] as string,
              )
                ? (row['COD_BANK'] as OrderExcelStatusPayment)
                : OrderExcelStatusPayment.COD

              let isRemote, isTour
              if (flashProvider && data) {
                const zipcode = row['Zipcode']
                isRemote = data.remotes.find(
                  (remote) =>
                    remote.subdistrict === subdistrict &&
                    remote.zipcode === zipcode,
                )
                isTour = data.tours.find(
                  (remote) =>
                    remote.subdistrict === subdistrict &&
                    remote.zipcode === zipcode,
                )
              }

              return {
                keyId: nanoid(),
                name: row['Name'] || '',
                product: row['Product'] || '',
                quantity: row['Unit']?.toString() || '',
                free: row['Free']?.toString() || '',
                discount: row['Discount']?.toString() || '',
                total: row['Total'] || 0,
                size: row['Size'] || '',
                cod_bank,
                address: address || '',
                subdistrict: subdistrict || '',
                district: row['Amphur'] || '',
                province: row['Province'] || '',
                zipcode: row['Zipcode'] || '',
                phone: cleanPhone(row['Phone'] || ''),
                actor: row['AddBy'] || '',
                date: row['Date'] || new Date().toISOString(),
                facebook: row['Facebook'] || '',
                channel: row['Channel'] || '',
                telesale: row['TeleSaleBy'] || '',
                isRemote: !!isRemote,
                isTour: !!isTour,
              }
            })
            .filter((row) => row.name && row.phone && row.product)

          if (lowerCaseKeys.length > 100) {
            inputFile.current!.value = ''
            message.error('ไม่สามารถนำเข้าข้อมูลเกิน 100 รายการต่อครั้ง')
            return
          }

          const uniqueArray = lowerCaseKeys.filter(
            (obj: any, index, self) =>
              index ===
              self.findIndex((o: any) =>
                Object.keys(obj)
                  .filter((key) => key !== 'keyId') // Exclude the `id` key
                  .every((key) => o[key] === obj[key]),
              ),
          )
          setOrderList(uniqueArray)
        }
      }
      reader.readAsArrayBuffer(file)
    }
  }

  const onChangeData = useCallback(
    (key: number, field: keyof IOrderListExcel, value: string | number) => {
      const newOrderList = [...orderList]
      newOrderList[key] = {
        ...newOrderList[key],
        [field]: value,
      }
      setOrderList(newOrderList)
    },
    [orderList],
  )

  const handleUpdateOrder = (data: IOrderListExcel, key: number) => {
    const newOrderList = [...orderList]
    newOrderList[key] = {
      ...data,
      field: '',
    }
    setOrderList(newOrderList)
  }

  const handleCheckCustomerWarning = async (index: number) => {
    try {
      const response = await axiosInstance.post('/blacklists/checking', {
        name: orderList[index].name,
        phoneNumber: orderList[index].phone,
        ignoreName: true,
      })
      if (response.data.blacklist) {
        dispatch(
          openModal({
            title: `เตือน! ลูกค้าอยู่ในรายการ Blacklist`,
            width: 500,
            content: (
              <ConfirmBlackListModal
                blackList={[orderList[index].name]}
                onConfirm={async () => {
                  await onHandleImportOrder(index)
                  dispatch(closeModal())
                }}
                onCancel={() =>
                  setOrderList((prev) => {
                    const newOrderList = [...prev]
                    newOrderList[index].field = 'name'
                    newOrderList[index].status = OrderExcelStatus.Warning
                    newOrderList[index].message = 'ลูกค้าอยู่ในรายการ Blacklist'
                    return newOrderList
                  })
                }
              />
            ),
          }),
        )
      } else {
        await onHandleImportOrder(index)
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const handleCheckCustomerListWarning = async ({
    ignoreDuplicate = false,
  }) => {
    const orderFilter = orderList.filter(
      (order) => order.status !== OrderExcelStatus.Success,
    )

    setPopupLoading(true)
    const chunkedOrders = chunk(orderFilter, 20)
    let blacklistsItems: any[] = []
    let blacklistsCount = 0
    for (const orders of chunkedOrders) {
      const namePhones = orders.map((order) => ({
        name: order.name,
        phoneNumber: order.phone,
      }))
      blacklistsCount += orders.length
      try {
        const response = await axiosInstance.post(
          '/blacklists/checking/multiple',
          {
            lists: namePhones,
            ignoreName: true,
          },
        )
        const processedBlacklist = response.data
        const blacklists = processedBlacklist.filter(
          (blacklist: any) => blacklist.blacklist,
        )
        blacklistsItems = [...blacklistsItems, ...blacklists]
        setPopupMessage(
          `กำลังตรวจสอบรายชื่อ Blacklist... ${numberFormat(
            blacklistsCount,
          )}/${numberFormat(orderFilter.length)}`,
        )
      } catch (e) {
        message.error('ไม่สามารถตรวจสอบ Blacklist ได้')
        setPopupLoading(false)
        return
      }
      await delayTime(1500)
    }
    setPopupLoading(false)

    const blackListsMapper = blacklistsItems.reduce(
      (acc: any, blacklist: any) => {
        const key = `${blacklist.name}${blacklist.phoneNumber}`
        acc[key] = blacklist
        return acc
      },
      {},
    )

    if (blacklistsItems.length > 0) {
      dispatch(
        openModal({
          title: `เตือน! ลูกค้าอยู่ในรายการ Blacklist`,
          content: (
            <ConfirmBlackListModal
              blackList={blacklistsItems.map(
                (blacklist: any) =>
                  `${blacklist.name} - ${blacklist.phoneNumber}`,
              )}
              onConfirm={async () => {
                await onHandleImportOrderList({ ignoreDuplicate })
                dispatch(closeModal())
              }}
              onConfirmWithoutBlacklist={async () => {
                await onHandleImportOrderList({
                  blackListsMapper,
                  ignoreDuplicate,
                })
                dispatch(closeModal())
              }}
              onCancel={() => {
                const newOrderList = orderList.map((order: any) => {
                  const key = `${order.name}${order.phone}`
                  const blacklist = blackListsMapper?.[key] ?? null
                  if (!blacklist || !blacklist?.blacklist) {
                    return order
                  }

                  return {
                    ...order,
                    field: 'name',
                    status: OrderExcelStatus.Warning,
                    message: 'ลูกค้าอยู่ในรายการ Blacklist',
                  }
                })
                setOrderList(newOrderList)
              }}
            />
          ),
        }),
      )
    } else {
      try {
        await onHandleImportOrderList({ ignoreDuplicate })
      } catch (e) {
        message.error('บันทึกข้อมูลไม่สำเร็จ')
        return
      }
    }

    return

    try {
      const namePhones = orderFilter.map((order) => ({
        name: order.name,
        phoneNumber: order.phone,
      }))

      const response = await axiosInstance.post(
        '/blacklists/checking/multiple',
        {
          lists: namePhones,
          ignoreName: true,
        },
      )

      const processedBlacklist = response.data
      const blacklists = processedBlacklist.filter(
        (blacklist: any) => blacklist.blacklist,
      )
      const blackListsMapper = blacklists.reduce((acc: any, blacklist: any) => {
        const key = `${blacklist.name}${blacklist.phoneNumber}`
        acc[key] = blacklist
        return acc
      }, {})
      if (blacklists.length > 0) {
        dispatch(
          openModal({
            title: `เตือน! ลูกค้าอยู่ในรายการ Blacklist`,
            content: (
              <ConfirmBlackListModal
                blackList={blacklists.map(
                  (blacklist: any) =>
                    `${blacklist.name} - ${blacklist.phoneNumber}`,
                )}
                onConfirm={async () => {
                  await onHandleImportOrderList({ ignoreDuplicate })
                  dispatch(closeModal())
                }}
                onConfirmWithoutBlacklist={async () => {
                  await onHandleImportOrderList({
                    blackListsMapper,
                    ignoreDuplicate,
                  })
                  dispatch(closeModal())
                }}
                onCancel={() => {
                  const newOrderList = orderList.map((order: any) => {
                    const key = `${order.name}${order.phone}`
                    const blacklist = blackListsMapper?.[key] ?? null
                    if (!blacklist || !blacklist?.blacklist) {
                      return order
                    }

                    return {
                      ...order,
                      field: 'name',
                      status: OrderExcelStatus.Warning,
                      message: 'ลูกค้าอยู่ในรายการ Blacklist',
                    }
                  })
                  setOrderList(newOrderList)
                }}
              />
            ),
          }),
        )
      } else {
        await onHandleImportOrderList({ ignoreDuplicate })
      }
    } catch (e) {
      message.error('บันทึกข้อมูลไม่สำเร็จ')
    }
  }

  const onHandleImportOrderList = async (args?: any) => {
    const { blackListsMapper = {}, ignoreDuplicate = false } = args ?? {}
    const orderFilter = orderList.filter((order) => {
      if (order.status !== OrderExcelStatus.Success) {
        const key = `${order.name}${order.phone}`
        if (
          blackListsMapper &&
          !isEmpty(blackListsMapper) &&
          blackListsMapper?.[key]?.blacklist
        ) {
          return false
        }
        return true
      }
    })

    const actorUsernames = orderFilter.map((order) => order.actor)
    const telesaleUsernames = orderFilter.map((order) => order.telesale)
    const usernames = uniq([...actorUsernames, ...telesaleUsernames])

    setPopupLoading(true)
    const chuckUsers = chunk(usernames, 10)
    let userCount = 0
    let users: any[] = []
    for (const user of chuckUsers) {
      userCount += user.length
      try {
        const reqActor = await axiosInstance.post(`/users/usernames/exist`, {
          usernames: user.filter(Boolean),
        })
        users = [...users, ...reqActor.data]
        setPopupMessage(
          `กำลังตรวจสอบรายชื่อผู้ดำเนินการ... ${numberFormat(
            userCount,
          )}/${numberFormat(usernames.length)}`,
        )
      } catch (error) {
        message.error(`ไม่สามารถตรวจสอบชื่อผู้ดำเนินการได้`)
        setPopupLoading(false)
        return
      }
      await delayTime(1500)
    }
    setPopupLoading(false)
    if (isEmpty(users)) {
      message.error(`ไม่พบข้อมูลผู้ดำเนินการ`)
      return
    }

    const userMapper = users.reduce((acc: any, user: any) => {
      acc[user.username] = user
      return acc
    }, {})

    let processedOrders = []
    let errors = false

    processedOrders = orderFilter
      .map((order, index) => {
        const actor = userMapper?.[order.actor] ?? null

        if ((actor && actor?.exist) || !order.actor) {
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = ''
            return newOrderList
          })
        } else {
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = 'actor'
            newOrderList[index].status = OrderExcelStatus.Warning
            newOrderList[index].message = `ไม่พบผู้ดำเนินการ`
            return newOrderList
          })
          return
        }

        let telesale = userMapper?.[order.telesale] ?? null

        if ((telesale && telesale?.exist) || !order.telesale) {
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = ''
            return newOrderList
          })
        } else {
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = 'telesale'
            newOrderList[index].status = OrderExcelStatus.Warning
            newOrderList[index].message =
              `ไม่พบ Telesale ชื่อ ${order.telesale}`
            return newOrderList
          })
          return
        }

        return {
          ...order,
          actor: order.actor,
          telesale: order.telesale,
          product: order.product.toString(),
          message: undefined,
          status: undefined,
          cod_bank: order.cod_bank,
        }
      })
      .filter(Boolean)

    if (isEmpty(processedOrders)) {
      message.error('ข้อมูลนำเข้าไม่ถูกต้อง')
      return
    }

    setPopupLoading(true)
    const chuckProcessOrders = chunk(processedOrders, 10)
    let responses: any[] = []
    let orderCount = 0
    for (const orders of chuckProcessOrders) {
      orderCount += orders.length
      try {
        const response = await axiosInstance.post<
          {
            field?: string
            status: OrderExcelStatus
            message?: string
          }[]
        >('/order/import-excel/batch', {
          orders,
          ignoreDuplicate: ignoreDuplicate ? 1 : 0,
        })
        responses = [...responses, ...response.data]
        setPopupMessage(
          `กำลังนำเข้าข้อมูล... ${numberFormat(orderCount)}/${numberFormat(
            processedOrders.length,
          )}`,
        )
      } catch (e) {}
      await delayTime(1500)
    }
    setPopupLoading(false)

    if (isEmpty(responses)) {
      message.error('ไม่สามารถนำเข้าข้อมูลได้ กรุณาตรวจสอบข้อมูลให้ถูกต้อง')
      return
    }

    const processedOrdersMapper = responses.reduce((acc: any, order: any) => {
      acc[order.key] = order
      return acc
    }, {})

    const newOrderList = orderList.map((order) => {
      const key = order.keyId ?? ''
      const processedOrder = processedOrdersMapper?.[key] ?? null
      if (processedOrder) {
        order.field = processedOrder.field
        order.status = processedOrder.status
        order.message = processedOrder.message
      }
      return order
    })

    const isDuplicate = orderList.some((order) => order.field === 'duplicate')
    setHasDuplicate(isDuplicate)

    const someSuccess = responses.some(
      (res) => res.status === OrderExcelStatus.Success,
    )
    if (someSuccess) {
      setOrderList(
        newOrderList.sort((a, b) =>
          a.status === OrderExcelStatus.Success ? 1 : -1,
        ),
      )
    } else {
      setOrderList(newOrderList)
    }

    message.info(
      `นำเข้าข้อมูลสำเร็จ ${
        responses.filter((res) => res.status === OrderExcelStatus.Success)
          .length
      } รายการ นำเข้าข้อมูลไม่สำเร็จ ${
        responses.filter((res) => res.status !== OrderExcelStatus.Success)
          .length
      } รายการ`,
    )

    return

    try {
      const response = await axiosInstance.post<
        {
          field?: string
          status: OrderExcelStatus
          message?: string
        }[]
      >('/order/import-excel', {
        orders: processedOrders,
        ignoreDuplicate: ignoreDuplicate ? 1 : 0,
      })

      // const newOrderList = [...orderList]
      const processedOrdersMapper = response.data.reduce(
        (acc: any, order: any) => {
          acc[order.key] = order
          return acc
        },
        {},
      )

      const newOrderList = orderList.map((order) => {
        const key = order.keyId ?? ''
        const processedOrder = processedOrdersMapper?.[key] ?? null
        if (processedOrder) {
          order.field = processedOrder.field
          order.status = processedOrder.status
          order.message = processedOrder.message
        }
        return order
      })

      const isDuplicate = orderList.some((order) => order.field === 'duplicate')
      setHasDuplicate(isDuplicate)

      // response.data.forEach((res, index) => {
      //   newOrderList[index].field = res.field
      //   newOrderList[index].status = res.status
      //   newOrderList[index].message = res.message
      // })
      const someSuccess = response.data.some(
        (res) => res.status === OrderExcelStatus.Success,
      )
      if (someSuccess) {
        setOrderList(
          newOrderList.sort((a, b) =>
            a.status === OrderExcelStatus.Success ? 1 : -1,
          ),
        )
      } else {
        setOrderList(newOrderList)
      }

      message.info(
        `นำเข้าข้อมูลสำเร็จ ${
          response.data.filter((res) => res.status === OrderExcelStatus.Success)
            .length
        } รายการ นำเข้าข้อมูลไม่สำเร็จ ${
          response.data.filter((res) => res.status !== OrderExcelStatus.Success)
            .length
        } รายการ`,
      )
    } catch (e: any) {
      // console.error('Error importing orders:', error)
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const onHandleImportOrder = async (index: number) => {
    try {
      let actor
      if (orderList[index].actor) {
        try {
          const reqActor = await axiosInstance.get(
            `/users/${orderList[index].actor}`,
          )
          actor = reqActor.data.username
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = ''
            return newOrderList
          })
        } catch (error) {
          message.error(`ไม่พบผู้ดำเนินการ ชื่อ ${orderList[index].actor}`)
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = 'actor'
            newOrderList[index].status = OrderExcelStatus.Warning
            newOrderList[index].message = `ไม่พบผู้ดำเนินการ`
            return newOrderList
          })
          throw new Error('Actor not found')
        }
      }

      let telesale
      if (orderList[index].telesale) {
        try {
          const reqTelesale = await axiosInstance.get(
            `/users/${orderList[index].telesale}`,
          )
          telesale = reqTelesale.data.username
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = ''
            return newOrderList
          })
        } catch (error) {
          message.error(`ไม่พบ Telesale ชื่อ ${orderList[index].telesale}`)
          setOrderList((prev) => {
            const newOrderList = [...prev]
            newOrderList[index].field = 'telesale'
            newOrderList[index].status = OrderExcelStatus.Warning
            newOrderList[index].message =
              `ไม่พบ Telesale ชื่อ ${orderList[index].telesale}`
            return newOrderList
          })
          throw new Error('Telesale not found')
        }
      }

      const response = await axiosInstance.post<
        {
          field?: string
          status: OrderExcelStatus
          message?: string
        }[]
      >('/order/import-excel', {
        orders: [
          {
            ...orderList[index],
            message: undefined,
            actor,
            product: orderList[index].product.toString(),
            telesale,
            status: undefined,
            cod_bank:
              orderList[index].cod_bank === OrderExcelStatusPayment.COD
                ? true
                : false,
          },
        ],
      })
      const newOrderList = [...orderList]
      newOrderList[index].field = response.data[0].field
      newOrderList[index].status = response.data[0].status
      newOrderList[index].message = response.data[0].message
      const someSuccess = response.data.some(
        (res) => res.status === OrderExcelStatus.Success,
      )
      if (someSuccess) {
        setOrderList(
          newOrderList.sort((a, b) =>
            a.status === OrderExcelStatus.Success ? 1 : -1,
          ),
        )
      } else {
        setOrderList(newOrderList)
      }
      if (response.data[0].status === OrderExcelStatus.Success) {
        message.success(
          `นำเข้าข้อมูลสำเร็จ ${
            response.data[0].status === OrderExcelStatus.Success ? 1 : 0
          } รายการ`,
        )
      } else {
        message.warning(`นำเข้าข้อมูลไม่สำเร็จ ${response.data[0].message}`)
      }
    } catch (e: any) {
      const messageError =
        ERRORS_MESSAGE_MAPPER?.[e.response?.data?.message ?? ''] ||
        'บันทึกข้อมูลไม่สำเร็จ'
      message.error(messageError)
    }
  }

  const handleSetOpenEditOrder = (order: IOrderListExcel, key: number) => {
    setEditOrder({
      ...order,
      key,
    })
  }

  const TextEditable = ({
    order,
    value,
    keyName,
    index,
    defaultValue = 'ไม่ระบุ',
  }: {
    order: IOrderListExcel
    value: any
    keyName: string
    index: number
    defaultValue?: string
  }) => {
    const isSuccess = orderList[index].status === OrderExcelStatus.Success
    return (
      <Box
        sx={{
          cursor: !isSuccess ? 'pointer' : 'default',
        }}
        onClick={() => {
          if (isSuccess) {
            return
          }
          handleSetOpenEditOrder(order, index)
        }}
        color={orderList[index].field == keyName ? '#f00' : 'inherit'}
      >
        <Typography
          fontSize={13}
          sx={{
            textDecoration: isSuccess ? 'none' : 'underline',
          }}
        >
          {value || defaultValue}
        </Typography>
        {orderList[index].field == keyName && !isSuccess && (
          <Typography
            fontSize={11}
            sx={{ textDecoration: 'none' }}
            color="error"
          >
            {orderList[index].message}
          </Typography>
        )}
      </Box>
    )
  }

  const RemoteMessage = ({ order }: { order: IOrderListExcel }) => {
    return (
      <>
        {order.isRemote ? (
          <Typography fontSize={12} color={'error'}>
            ** ที่อยู่นี้เป็นพื้นที่ห่างไกลของ Flash **
          </Typography>
        ) : (
          <></>
        )}
        {order.isToure ? (
          <Typography fontSize={12} color={'error'}>
            ** ที่อยู่นี้เป็นพื้นที่ท่องเที่ยวของ Flash **
          </Typography>
        ) : (
          <></>
        )}
      </>
    )
  }

  const columns: TableColumnsType<
    IOrderListExcel & { key: number; numberRow: number }
  > = [
    {
      dataIndex: 'key',
      key: 'action',
      width: 100,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              gap={1}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {/* <IconButton disabled={true}>
                {orderList[key].status === OrderExcelStatus.Success ? (
                  <CheckCircleOutlineIcon fontSize="large" color="primary" />
                ) : orderList[key].status === OrderExcelStatus.Warning ? (
                  <RotateLeftIcon fontSize="large" color="warning" />
                ) : (
                  <SaveIcon fontSize="large" color="success" />
                )}
              </IconButton> */}
              {orderList[key].status === OrderExcelStatus.Success && (
                <IconButton disabled={true}>
                  <CheckCircleOutlineIcon fontSize="large" color="primary" />
                </IconButton>
              )}
              {orderList[key].status === OrderExcelStatus.Warning && (
                <IconButton disabled={true}>
                  <WarningIcon fontSize="large" color="warning" />
                </IconButton>
              )}
              {![OrderExcelStatus.Warning, OrderExcelStatus.Success].includes(
                orderList[key].status as OrderExcelStatus,
              ) && (
                <IconButton
                  onClick={() => {
                    handleSetOpenEditOrder(order, key)
                  }}
                >
                  <EditIcon color="primary" />
                </IconButton>
              )}
            </Box>
            <Box
              sx={{
                minWidth: 100,
                textAlign: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{ lineHeight: 1.4, display: 'block' }}
                color="#ff0000"
              >
                {orderList[key].message}
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      title: 'ลำดับ',
      dataIndex: 'numberRow',
      key: 'numberRow',
      align: 'center' as const,
      width: 80,
      render: (numberRow: number) => {
        return (
          <Box>
            <Typography
              variant="subtitle1"
              color="text.secondary"
              textAlign={'center'}
            >
              {numberRow}
            </Typography>
          </Box>
        )
      },
    },
    {
      title: 'สินค้า',
      dataIndex: 'key',
      key: 'product',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.product}
              order={order}
              keyName="product"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'จำนวน',
      dataIndex: 'key',
      key: 'quantity',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.quantity}
              order={order}
              keyName="quantity"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'ฟรี (0/1)',
      dataIndex: 'key',
      key: 'free',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.free}
              order={order}
              keyName="free"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'ส่วนลด',
      dataIndex: 'key',
      key: 'discount',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.discount}
              order={order}
              keyName="discount"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'ขนาด',
      dataIndex: 'key',
      key: 'size',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.size}
              order={order}
              keyName="size"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'ราคารวม',
      dataIndex: 'key',
      key: 'total',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.total}
              order={order}
              keyName="total"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'ชื่อผู้รับ',
      dataIndex: 'key',
      key: 'name',
      align: 'left' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 120,
            }}
          >
            <TextEditable
              value={order.name}
              order={order}
              keyName="name"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'โทร',
      dataIndex: 'key',
      key: 'phone',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.phone}
              order={order}
              keyName="phone"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'ที่อยู่',
      dataIndex: 'key',
      key: 'address',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 130,
            }}
          >
            <TextEditable
              value={order.address}
              order={order}
              keyName="address"
              index={key}
            />
            <RemoteMessage order={order} />
          </Box>
        )
      },
    },
    {
      title: 'ตำบล',
      dataIndex: 'key',
      key: 'subdistrict',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.subdistrict}
              order={order}
              keyName="address"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'อำเภอ',
      dataIndex: 'key',
      key: 'district',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.district}
              order={order}
              keyName="address"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'จังหวัด',
      dataIndex: 'key',
      key: 'province',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.province}
              order={order}
              keyName="address"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'รหัสไปรษณีย์',
      dataIndex: 'key',
      key: 'zipcode',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.zipcode}
              order={order}
              keyName="address"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'Facebook',
      dataIndex: 'key',
      key: 'facebook',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.facebook}
              order={order}
              keyName="facebook"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'รูปแบบการชำระเงิน',
      dataIndex: 'key',
      key: 'cod_bank',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.cod_bank}
              order={order}
              keyName="cod_bank"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'โดย',
      dataIndex: 'key',
      key: 'actor',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.actor}
              order={order}
              keyName="actor"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'วันที่',
      dataIndex: 'key',
      key: 'date',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={
                order.date
                  ? dayjs(order.date).format(DATE_FORMAT_INTERNATIONAL)
                  : ''
              }
              order={order}
              keyName="date"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'ช่องทางการขาย',
      dataIndex: 'key',
      key: 'channel',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 100,
            }}
          >
            <TextEditable
              value={order.channel}
              order={order}
              keyName="channel"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      title: 'TELESALE',
      dataIndex: 'key',
      key: 'telesale',
      align: 'center' as const,
      render: (key: number, order: IOrderListExcel) => {
        return (
          <Box
            sx={{
              minWidth: 80,
            }}
          >
            <TextEditable
              value={order.telesale}
              order={order}
              keyName="telesale"
              index={key}
            />
          </Box>
        )
      },
    },
    {
      dataIndex: 'key',
      key: 'action',
      width: 60,
      align: 'center' as const,
      render: (key: number) => {
        return (
          <Box>
            <IconButton
              onClick={() => {
                const newOrderList = [...orderList]
                newOrderList.splice(key, 1)
                setOrderList(newOrderList)
              }}
            >
              <DeleteIcon color="error" />
            </IconButton>
          </Box>
        )
      },
    },
  ]

  return (
    <ContentWrapper>
      <CircularLoading message={popupMessage} isLoading={popupLoading} />
      <OrderFormImport
        key={editRow}
        order={editOrder}
        isOpen={!!editOrder}
        onAdd={handleUpdateOrder}
        onClose={() => {
          setEditOrder(null)
        }}
      />
      <Helmet title="เพิ่มรายการคำสั่งซื้อใหม่ด้วย Excel" />
      <Grid container justifyContent="flex-start" alignItems="center">
        <Grid item>
          <HeaderContent
            goBack
            title="เพิ่มรายการคำสั่งซื้อใหม่ด้วย Excel"
            routes={Menus}
            descriptions="เพิ่มรายการคำสั่งซื้อใหม่ด้วย Excel"
          />
        </Grid>
      </Grid>
      <Card
        className="import-excel-table"
        sx={{
          padding: 2,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: orderList.length > 0 ? 'auto' : '300px',
        }}
      >
        {orderList.length > 0 ? (
          <>
            <Table
              size="small"
              columns={columns}
              scroll={{
                x: columns.reduce(
                  (pre, cur: any) => (cur.width ? cur.width + pre : pre),
                  0,
                ),
              }}
              dataSource={orderList?.map(
                (order: IOrderListExcel, index: number) => {
                  return {
                    ...order,
                    key: index,
                    numberRow: index + 1,
                  }
                },
              )}
              bordered
              pagination={{ hideOnSinglePage: true }}
            />
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 2,
              }}
            >
              <Button
                disabled={
                  !orderList.some(
                    (order) => order.status !== OrderExcelStatus.Success,
                  )
                }
                variant="contained"
                color="success"
                size="large"
                sx={{
                  marginRight: 2,
                }}
                startIcon={<SaveIcon fontSize="large" />}
                onClick={() => {
                  handleCheckCustomerListWarning({
                    ignoreDuplicate: false,
                  })
                }}
              >
                บันทึกข้อมูล
              </Button>
              {hasDuplicate && (
                <Button
                  disabled={
                    !orderList.some(
                      (order) => order.status !== OrderExcelStatus.Success,
                    )
                  }
                  variant="contained"
                  color="info"
                  size="large"
                  sx={{
                    marginRight: 2,
                  }}
                  startIcon={<SaveIcon fontSize="large" />}
                  onClick={() => {
                    handleCheckCustomerListWarning({
                      ignoreDuplicate: true,
                    })
                  }}
                >
                  บันทึกข้อมูลโดยไม่สนใจคำสั่งซื้อซ้ำ
                </Button>
              )}
              <Button
                variant="contained"
                color="error"
                size="large"
                startIcon={<DeleteForeverIcon fontSize="large" />}
                onClick={() => {
                  setOrderList([])
                }}
              >
                ยกเลิก
              </Button>
            </Box>
          </>
        ) : (
          <>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '300px',
                height: '100px',
              }}
            >
              <input
                style={{
                  padding: '10px',
                }}
                ref={inputFile}
                type="file"
                name="file"
                className="custom-input-file"
                required
                onChange={handleImport}
                accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              />
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                textAlign: 'center',
                maxWidth: '550px',
                minWidth: '300px',
              }}
            >
              <Typography variant="body1">หมายเหตุ</Typography>
              <Typography variant="body2" color="text.secondary">
                ช่องสินค้า หากมีจำนวนสินค้ามากกว่า 1 ให้ใส่เครื่องหมาย comma (,)
                เช่นมีสินค้า 2 รายการ ให้กำหนดเป็น A1,A2 และในกรณีนี่ ช่อง
                จำนวน,ฟรี,ส่วนลด หรือขนาด สามารถกำหนดแยกได้เช่นกันโดยใส่
                เครื่องหมาย comma (,)
                หากไม่ต้องการกำหนดแยกให้ใส่เป็นจำนวนเดียวโดยไม่มีเครื่องหมาย
                comma (,) คั่น{' '}
              </Typography>
              <Button
                variant="outlined"
                color="info"
                size="large"
                startIcon={<GridOnIcon fontSize="large" />}
                component={'a'}
                href={'/excels/import.xlsx'}
                // onClick={() => {
                //   downloadExcel()
                // }}
              >
                ดาวน์โหลดตัวอย่างไฟล์
              </Button>
            </Box>
          </>
        )}
      </Card>
    </ContentWrapper>
  )
}

export default OrderImportExcelPage
